import {
  BILLING_INTERVALS,
  BOOK_ORDER_STATUSES,
  CANCELLATION_REASONS,
  SHIPPING_COUNTRIES,
  CANCELLATION_TEXT_MAX,
  RENEWAL_MODES,
  SUBSCRIPTION_EFFECTS,
  SUBSCRIPTION_STATUSES,
  feedbackAnswerProblem,
  repeatedFeedbackAnswers,
} from '@oefy/core';
import { z } from 'zod';
import { avatarColorSchema, avatarSchema, emailSchema } from './auth';
import { pageFields, pageQuery, schoolGroupSchema, timestampSchema } from './common';

export const subscriptionStatusSchema = z.enum(SUBSCRIPTION_STATUSES);
export const integrationSourceSchema = z.enum(['woocommerce', 'mollie', 'oefy']);
export const integrationStatusSchema = z.enum([
  'received',
  'processed',
  'ignored',
  'failed',
  'held',
]);

export const subscriptionViewSchema = z.object({
  id: z.uuid(),
  status: subscriptionStatusSchema,
  productName: z.string(),
  priceCents: z.int(),
  currency: z.string(),
  interval: z.enum(BILLING_INTERVALS),
  paidUntil: timestampSchema,
  trialUntil: timestampSchema.nullable(),
  graceUntil: timestampSchema.nullable(),
  cancelledAt: timestampSchema.nullable(),
  renewsAutomatically: z.boolean(),
  renewal: z.enum(RENEWAL_MODES),
  renewedBy: z.object({ name: z.string(), url: z.string().nullable() }).nullable(),
  canCancel: z.boolean(),
  canPay: z.boolean(),
  child: z
    .object({
      id: z.uuid(),
      firstName: z.string(),
      schoolGroup: schoolGroupSchema,
      extraGroup: z.boolean(),
    })
    .nullable(),
});

export const familyBillingSchema = z.object({
  subscriptions: z.array(subscriptionViewSchema),
  hasAccess: z.boolean(),
  grantedUntil: timestampSchema.nullable(),
});

export const checkoutSchema = z.object({ checkoutUrl: z.url() });

export const cancellationReasonSchema = z.enum(CANCELLATION_REASONS);

const FEEDBACK_PROBLEMS = {
  empty: 'Answer this question',
  too_short: 'Write at least six letters or digits',
  filler: 'Write a real answer',
} as const;

const feedbackAnswer = z
  .string()
  .trim()
  .max(CANCELLATION_TEXT_MAX)
  .superRefine((value, context) => {
    const problem = feedbackAnswerProblem(value);
    if (problem) {
      context.addIssue({ code: 'custom', message: FEEDBACK_PROBLEMS[problem] });
    }
  });

export const CANCELLATION_QUESTIONS = ['reasonText', 'missed', 'improve'] as const;

export const cancelSubscriptionInputSchema = z
  .object({
    reasonText: feedbackAnswer,
    missed: feedbackAnswer,
    improve: feedbackAnswer,
  })
  .superRefine((input, context) => {
    const answers = CANCELLATION_QUESTIONS.map((field) => input[field]);
    for (const index of repeatedFeedbackAnswers(answers)) {
      context.addIssue({
        code: 'custom',
        message: 'Give a different answer to each question',
        path: [CANCELLATION_QUESTIONS[index] ?? 'improve'],
      });
    }
  });

const cancellationFeedbackFields = {
  reason: cancellationReasonSchema.nullable(),
  reasonText: z.string().nullable(),
  missed: z.string().nullable(),
  improve: z.string().nullable(),
  createdAt: timestampSchema,
  cancelledBy: z.string().nullable(),
};

export const FAMILY_ACTIVITY = ['active', 'quiet', 'never'] as const;
export const FAMILY_ACCESS = ['with', 'without', 'grant_only'] as const;
export const FAMILY_MOMENTS = ['trial', 'renews_soon', 'ends_soon'] as const;
export const TEST_ACCOUNT_FILTERS = ['hide', 'only'] as const;
export const CANCELLED_BY = ['parent', 'staff'] as const;

const queryGroup = z.coerce.number().pipe(schoolGroupSchema).optional();

export const adminUserListQuerySchema = z.object({
  search: z.string().trim().max(200).optional(),
  status: z.enum([...SUBSCRIPTION_STATUSES, 'none']).optional(),
  activity: z.enum(FAMILY_ACTIVITY).optional(),
  access: z.enum(FAMILY_ACCESS).optional(),
  shop: z.uuid().optional(),
  product: z.uuid().optional(),
  schoolGroup: queryGroup,
  moment: z.enum(FAMILY_MOMENTS).optional(),
  test: z.enum(TEST_ACCOUNT_FILTERS).optional(),
  ...pageQuery,
});

export const adminUserRowSchema = z.object({
  id: z.uuid(),
  email: z.string(),
  name: z.string().nullable(),
  photoUrl: z.string().nullable(),
  children: z.array(
    z.object({
      firstName: z.string(),
      schoolGroup: schoolGroupSchema,
      avatar: avatarSchema,
      avatarColor: avatarColorSchema,
      photoUrl: z.string().nullable(),
    }),
  ),
  productName: z.string().nullable(),
  subscriptionStatus: subscriptionStatusSchema.nullable(),
  paidUntil: timestampSchema.nullable(),
  hasAccess: z.boolean(),
  accessViaGrant: z.boolean(),
  testAccount: z.boolean(),
  lastLoginAt: timestampSchema.nullable(),
  lastPracticedAt: timestampSchema.nullable(),
  roundsLast30: z.int().min(0),
  createdAt: timestampSchema,
});

export const adminUserListSchema = z.object({
  users: z.array(adminUserRowSchema),
  ...pageFields,
});

export const adminUserDetailSchema = z.object({
  user: z.object({
    id: z.uuid(),
    email: z.string(),
    name: z.string().nullable(),
    photoUrl: z.string().nullable(),
    createdAt: timestampSchema,
    lastLoginAt: timestampSchema.nullable(),
    disabledAt: timestampSchema.nullable(),
    testAccount: z.boolean(),
  }),
  hasAccess: z.boolean(),
  children: z.array(
    z.object({
      id: z.uuid(),
      firstName: z.string(),
      schoolGroup: schoolGroupSchema,
      avatar: avatarSchema,
      avatarColor: avatarColorSchema,
      photoUrl: z.string().nullable(),
      createdAt: timestampSchema,
      groups: z.array(schoolGroupSchema),
      groupCheckDue: z.boolean(),
      activity: z.object({
        lastPracticedAt: timestampSchema.nullable(),
        rounds: z.int().min(0),
        games: z.array(
          z.object({
            name: z.string(),
            completed: z.int().min(0),
            bestStars: z.int().min(1).max(3),
          }),
        ),
        openGames: z.array(
          z.object({
            name: z.string(),
            position: z.int().min(0).max(9),
            startedAt: timestampSchema,
          }),
        ),
        goalsReached: z.int().min(0),
        days: z.array(z.object({ day: z.iso.date(), rounds: z.int().min(0) })),
        subjects: z.array(
          z.object({
            name: z.string(),
            rounds: z.int().min(0),
            accuracy: z.number().nullable(),
          }),
        ),
        openRounds: z.array(
          z.object({
            subjectName: z.string(),
            mode: z.enum(['practice', 'test']),
            answered: z.int().min(0),
            total: z.int().min(1),
            startedAt: timestampSchema,
          }),
        ),
      }),
    }),
  ),
  subscriptions: z.array(
    z.object({
      id: z.uuid(),
      status: subscriptionStatusSchema,
      productName: z.string(),
      priceCents: z.int(),
      currency: z.string(),
      interval: z.enum(BILLING_INTERVALS),
      paidUntil: timestampSchema,
      graceUntil: timestampSchema.nullable(),
      cancelledAt: timestampSchema.nullable(),
      endedAt: timestampSchema.nullable(),
      replacedBy: z.uuid().nullable(),
      child: z
        .object({
          id: z.uuid(),
          firstName: z.string(),
          schoolGroup: schoolGroupSchema,
          assigned: z.boolean(),
        })
        .nullable(),
      sourceOrderId: z.string(),
      providerCustomerId: z.string().nullable(),
      providerSubscriptionId: z.string().nullable(),
      renewal: z.enum(RENEWAL_MODES),
      canCancel: z.boolean(),
      canRestart: z.boolean(),
      createdAt: timestampSchema,
      feedback: z.object(cancellationFeedbackFields).nullable(),
      bookOrder: z
        .object({
          id: z.uuid(),
          status: z.enum(BOOK_ORDER_STATUSES),
          schoolGroup: schoolGroupSchema,
          sku: z.string().nullable(),
          shopOrderId: z.string().nullable(),
          lastError: z.string().nullable(),
          shipping: z.object({
            name: z.string(),
            street: z.string(),
            houseNumber: z.string(),
            postcode: z.string(),
            city: z.string(),
            country: z.enum(SHIPPING_COUNTRIES),
          }),
          createdAt: timestampSchema,
          placedAt: timestampSchema.nullable(),
        })
        .nullable(),
    }),
  ),
  payments: z.array(
    z.object({
      id: z.uuid(),
      purpose: z.enum(['initial', 'renewal', 'repair']),
      amountCents: z.int(),
      currency: z.string(),
      status: z.string(),
      providerPaymentId: z.string(),
      paidAt: timestampSchema.nullable(),
      createdAt: timestampSchema,
    }),
  ),
  grants: z.array(
    z.object({
      id: z.uuid(),
      until: timestampSchema,
      note: z.string(),
      createdAt: timestampSchema,
      revokedAt: timestampSchema.nullable(),
      active: z.boolean(),
      grantedBy: z.uuid().nullable(),
    }),
  ),
  events: z.array(
    z.object({
      id: z.uuid(),
      source: integrationSourceSchema,
      eventKey: z.string(),
      summary: z.string(),
      detail: z.string().nullable(),
      status: integrationStatusSchema,
      error: z.string().nullable(),
      receivedAt: timestampSchema,
    }),
  ),
  followUps: z.array(
    z.object({
      id: z.uuid(),
      effect: z.enum(SUBSCRIPTION_EFFECTS),
      status: z.enum(['pending', 'done', 'failed', 'ignored']),
      attempts: z.int(),
      lastError: z.string().nullable(),
      createdAt: timestampSchema,
      completedAt: timestampSchema.nullable(),
    }),
  ),
  notes: z.array(
    z.object({
      id: z.uuid(),
      body: z.string(),
      author: z.object({ id: z.uuid(), name: z.string() }).nullable(),
      createdAt: timestampSchema,
      updatedAt: timestampSchema.nullable(),
    }),
  ),
  mails: z.array(
    z.object({
      id: z.uuid(),
      recipient: z.string(),
      tag: z.string().nullable(),
      subject: z.string(),
      status: z.enum(['sent', 'failed', 'withheld']),
      error: z.string().nullable(),
      delivery: z.enum(['delivered', 'delayed', 'bounced', 'complained']).nullable(),
      deliveryAt: timestampSchema.nullable(),
      createdAt: timestampSchema,
    }),
  ),
});

export const FAMILY_NOTE_MAX = 2000;
export const SUPPORT_LINK_HOURS = 24;

export const emailChangeInputSchema = z.object({
  email: emailSchema,
  note: z.string().trim().min(3).max(500),
  sendSignInLink: z.boolean().default(true),
});

export const staffCancelInputSchema = z.object({
  reason: cancellationReasonSchema,
  note: z.string().trim().min(3).max(500),
});

export const restartRenewalInputSchema = z.object({
  note: z.string().trim().min(3).max(500),
});

export const familyNoteInputSchema = z.object({
  body: z.string().trim().min(1).max(FAMILY_NOTE_MAX),
});

export const familySubscriptionParamsSchema = z.object({
  id: z.uuid(),
  subscriptionId: z.uuid(),
});

export const familyNoteParamsSchema = z.object({
  id: z.uuid(),
  noteId: z.uuid(),
});

export const grantInputSchema = z.object({
  until: z.iso.date(),
  note: z.string().trim().min(3).max(500),
});

export const childGroupInputSchema = z.object({
  schoolGroup: schoolGroupSchema,
});

export const familyChildParamsSchema = z.object({
  id: z.uuid(),
  childId: z.uuid(),
});

export const revokeInputSchema = z.object({
  note: z.string().trim().min(3).max(500),
});

export const integrationListQuerySchema = z.object({
  status: integrationStatusSchema.optional(),
  source: integrationSourceSchema.optional(),
  shop: z.uuid().optional(),
  search: z.string().trim().max(200).optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  ...pageQuery,
});

export const integrationRowSchema = z.object({
  id: z.uuid(),
  source: integrationSourceSchema,
  shop: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  eventKey: z.string(),
  summary: z.string(),
  detail: z.string().nullable(),
  status: integrationStatusSchema,
  attempts: z.int(),
  duplicates: z.int(),
  error: z.string().nullable(),
  userId: z.uuid().nullable(),
  userEmail: z.string().nullable(),
  receivedAt: timestampSchema,
  lastReceivedAt: timestampSchema,
  processedAt: timestampSchema.nullable(),
});

export const integrationListSchema = z.object({
  events: z.array(integrationRowSchema),
  ...pageFields,
});

export const cancellationListQuerySchema = z.object({
  reason: cancellationReasonSchema.optional(),
  search: z.string().trim().max(100).optional(),
  months: z.coerce.number().int().min(1).max(24).default(12),
  shop: z.uuid().optional(),
  interval: z.enum(BILLING_INTERVALS).optional(),
  schoolGroup: queryGroup,
  by: z.enum(CANCELLED_BY).optional(),
  ...pageQuery,
});

export const cancellationRowSchema = z.object({
  id: z.uuid(),
  ...cancellationFeedbackFields,
  interval: z.enum(BILLING_INTERVALS),
  schoolGroup: schoolGroupSchema.nullable(),
  subscribedDays: z.int().min(0),
  parent: z.object({ id: z.uuid(), email: z.string() }).nullable(),
});

export const cancellationOverviewSchema = z.object({
  reasons: z.array(z.object({ reason: cancellationReasonSchema, count: z.int().min(0) })),
  months: z.array(z.object({ month: z.string(), count: z.int().min(0) })),
  items: z.array(cancellationRowSchema),
  ...pageFields,
});

export const paymentExportQuerySchema = z.object({
  from: z.iso.date(),
  to: z.iso.date(),
  shop: z.uuid().optional(),
});

export type SubscriptionView = z.infer<typeof subscriptionViewSchema>;
export type CancelSubscriptionInput = z.input<typeof cancelSubscriptionInputSchema>;
export type CancelSubscriptionFields = z.output<typeof cancelSubscriptionInputSchema>;
export type CancellationListFilters = z.output<typeof cancellationListQuerySchema>;
export type CancellationListQuery = Partial<CancellationListFilters>;
export type CancellationOverview = z.infer<typeof cancellationOverviewSchema>;
export type CancellationRow = z.infer<typeof cancellationRowSchema>;
export type FamilyBilling = z.infer<typeof familyBillingSchema>;
export type AdminUserListFilters = z.output<typeof adminUserListQuerySchema>;
export type AdminUserListQuery = Partial<AdminUserListFilters>;
export type FamilyActivity = (typeof FAMILY_ACTIVITY)[number];
export type FamilyAccess = (typeof FAMILY_ACCESS)[number];
export type FamilyMoment = (typeof FAMILY_MOMENTS)[number];
export type AdminUserList = z.infer<typeof adminUserListSchema>;
export type AdminUserRow = z.infer<typeof adminUserRowSchema>;
export type AdminUserDetail = z.infer<typeof adminUserDetailSchema>;
export type GrantInput = z.infer<typeof grantInputSchema>;
export type RevokeInput = z.infer<typeof revokeInputSchema>;
export type ChildGroupInput = z.infer<typeof childGroupInputSchema>;
export type EmailChangeInput = z.input<typeof emailChangeInputSchema>;
export type EmailChangeFields = z.output<typeof emailChangeInputSchema>;
export type StaffCancelInput = z.infer<typeof staffCancelInputSchema>;
export type RestartRenewalInput = z.infer<typeof restartRenewalInputSchema>;
export type FamilyNoteInput = z.infer<typeof familyNoteInputSchema>;
export type IntegrationListFilters = z.output<typeof integrationListQuerySchema>;
export type IntegrationListQuery = Partial<IntegrationListFilters>;
export type IntegrationList = z.infer<typeof integrationListSchema>;
export type IntegrationRow = z.infer<typeof integrationRowSchema>;
export type IntegrationSource = z.infer<typeof integrationSourceSchema>;
export type IntegrationStatus = z.infer<typeof integrationStatusSchema>;

export const adminOverviewSchema = z.object({
  questions: z.object({
    draft: z.int(),
    published: z.int(),
    awaitingDifficulty: z.int(),
  }),
  readingTexts: z.object({ draft: z.int(), published: z.int() }),
  availability: z.object({ live: z.int(), ready: z.int(), total: z.int() }),
  families: z.object({
    total: z.int(),
    withAccess: z.int(),
    newToday: z.int(),
    newYesterday: z.int(),
    paymentFailed: z.int(),
  }),
  integrations: z.object({ failed: z.int(), waiting: z.int() }),
  reports: z.object({ open: z.int() }),
});

export type AdminOverview = z.infer<typeof adminOverviewSchema>;
